import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { FileSignature, Printer, UserCheck } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { useDocumentationClusters } from "../api/clusters";
import { CreateClusterDialog } from "../components/CreateClusterDialog";
import { BillingOrderDialog } from "../components/BillingOrderDialog";
import { MarkSettledDialog } from "../components/MarkSettledDialog";
import type { ClusterSigningMode, DocumentationCluster } from "../types";
import { CLUSTER_TYPE_LABELS } from "../types";

/** Signer display for the "Sprawdzający" column — covers all three signer kinds. */
function signerName(c: DocumentationCluster): string {
  return (
    c.contractor_name
    ?? c.employee_signer_name
    ?? c.reviewing_director_name
    ?? "—"
  );
}
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";

function clusterStatusColor(c: DocumentationCluster): "gray" | "yellow" | "green" {
  if (c.signing_mode === "paper") return "yellow";
  if (c.document_count === 0) return "gray";
  if (c.pending_count > 0) return "yellow";
  return "green";
}

function clusterStatusLabel(c: DocumentationCluster): string {
  if (c.signing_mode === "paper") return "Papierowe · Sprawdzone";
  if (c.document_count === 0) return "Brak dokumentów";
  if (c.pending_count > 0) return `${c.pending_count} oczekujących`;
  return "Wszystkie sprawdzone";
}

// Cluster-level status filter. Statuses are derived from the same counters used
// by the status badges; they are NOT mutually exclusive (e.g. a settled cluster
// can also be fully checked), so the filter matches on a predicate rather than a
// single canonical status.
type ClusterStatusFilter = "" | "pending" | "checked" | "empty" | "to_approve" | "settled";

function matchesClusterStatus(c: DocumentationCluster, filter: ClusterStatusFilter): boolean {
  switch (filter) {
    case "pending":
      return c.pending_count > 0;
    case "checked":
      return c.document_count > 0 && c.pending_count === 0;
    case "empty":
      return c.document_count === 0;
    case "to_approve":
      return c.pending_approval_count > 0;
    case "settled":
      return c.is_settled;
    default:
      return true;
  }
}

const SELECT_CLASS =
  "w-full sm:w-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500";

// Persist a piece of state to sessionStorage so filter selections survive
// navigating into a cluster detail and back (the list component unmounts on
// navigation, which would otherwise reset local state). Best-effort: any storage
// error falls back to the in-memory default.
function useSessionState<T>(key: string, initial: T) {
  const read = (): T => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  };
  const [value, setValue] = useState<T>(read);
  const keyRef = useRef(key);
  // Re-read when the storage key changes (e.g. switching project scope without
  // the component remounting).
  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      setValue(read());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* storage unavailable or full — persistence is best-effort */
    }
  }, [key, value]);
  return [value, setValue] as const;
}

interface ProjectBrief {
  id: string;
  name: string;
  short_name: string;
}

const SIGNING_MODE_OPTIONS: { value: ClusterSigningMode | ""; label: string }[] = [
  { value: "", label: "Wszystkie" },
  { value: "electronic", label: "Elektroniczne" },
  { value: "paper", label: "Papierowe" },
];

export function ClusterListPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCoordinator = user?.role === "coordinator";
  const isContractor = user?.role === "contractor";
  const isCEO = user?.is_ceo ?? false;
  const canManage = isDirector || isCoordinator || isCEO;
  const canCreateCluster = user?.has_doc_cluster_permission ?? false;

  const [showCreate, setShowCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBilling, setShowBilling] = useState(false);
  const [showMarkSettled, setShowMarkSettled] = useState(false);
  // Filters persist per view context (project scope) so they survive entering a
  // cluster and returning, and don't leak between the cross-project list and a
  // project-scoped one.
  const filterKey = (name: string) => `docClusterFilters:${projectId ?? "all"}:${name}`;
  const [signingModeFilter, setSigningModeFilter] = useSessionState<ClusterSigningMode | "">(filterKey("signingMode"), "");
  const [statusFilter, setStatusFilter] = useSessionState<ClusterStatusFilter>(filterKey("status"), "");
  const [projectFilter, setProjectFilter] = useSessionState<string>(filterKey("project"), "");
  const [signerFilter, setSignerFilter] = useSessionState<string>(filterKey("signer"), "");
  const [authorFilter, setAuthorFilter] = useSessionState<string>(filterKey("author"), "");
  const [onlyMine, setOnlyMine] = useSessionState<boolean>(filterKey("onlyMine"), false);

  const { data: clusters = [], isLoading, refetch } = useDocumentationClusters(projectId, signingModeFilter);

  // Options cover both signer kinds: external contractors ("c:") and
  // internal employee signers ("e:").
  const signerOptions = Array.from(
    new Map(
      clusters
        .filter((c) => c.contractor || c.employee_signer)
        .map((c): [string, string] =>
          c.contractor
            ? [`c:${c.contractor}`, c.contractor_name ?? ""]
            : [`e:${c.employee_signer}`, `${c.employee_signer_name ?? ""} (pracownik)`]
        )
    ).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // Project ("Przedsięwzięcie") options — only relevant on the cross-project
  // listing; on a project-scoped route the list already holds a single project.
  const projectOptions = Array.from(
    new Map(clusters.map((c): [string, string] => [c.project, c.project_name])).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // Author options — built from the clusters the user is allowed to see. Only
  // directors get this filter (they are the role with full cross-author visibility).
  const authorOptions = Array.from(
    new Map(
      clusters
        .filter((c) => c.created_by)
        .map((c): [string, string] => [c.created_by as string, c.created_by_name ?? ""])
    ).entries()
  ).sort((a, b) => a[1].localeCompare(b[1]));

  // "Do akceptacji" only makes sense for managers who act on approvals.
  const statusOptions: { value: ClusterStatusFilter; label: string }[] = [
    { value: "", label: "Wszystkie statusy" },
    { value: "pending", label: "Oczekujące na sprawdzenie" },
    { value: "checked", label: "Sprawdzone" },
    { value: "empty", label: "Brak dokumentów" },
    ...(canManage ? [{ value: "to_approve" as const, label: "Do akceptacji" }] : []),
    { value: "settled", label: "Rozliczone" },
  ];

  const visibleClusters = clusters.filter((c) => {
    if (signerFilter) {
      const match = signerFilter.startsWith("c:")
        ? c.contractor === signerFilter.slice(2)
        : c.employee_signer === signerFilter.slice(2);
      if (!match) return false;
    }
    if (!projectId && projectFilter && c.project !== projectFilter) return false;
    if (authorFilter && c.created_by !== authorFilter) return false;
    if (!matchesClusterStatus(c, statusFilter)) return false;
    // "Tylko moje" — only clusters authored by the logged-in user (match on author id).
    if (onlyMine && c.created_by !== user?.id) return false;
    return true;
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const [scrollWidth, setScrollWidth] = useState(0);

  const clusterCount = visibleClusters.length;

  useEffect(() => {
    const scroller = scrollRef.current;
    const sticky = stickyScrollRef.current;
    if (!scroller || !sticky) return;

    const syncFromScroller = () => { sticky.scrollLeft = scroller.scrollLeft; };
    const syncFromSticky = () => { scroller.scrollLeft = sticky.scrollLeft; };

    scroller.addEventListener("scroll", syncFromScroller);
    sticky.addEventListener("scroll", syncFromSticky);
    return () => {
      scroller.removeEventListener("scroll", syncFromScroller);
      sticky.removeEventListener("scroll", syncFromSticky);
    };
  }, [clusterCount]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    setScrollWidth(scroller.scrollWidth);
    const obs = new ResizeObserver(() => setScrollWidth(scroller.scrollWidth));
    obs.observe(scroller);
    return () => obs.disconnect();
  }, [clusterCount]);

  const { data: project } = useQuery({
    queryKey: ["project-brief", projectId],
    queryFn: () =>
      apiClient
        .get<{ data: ProjectBrief }>(`/enterprises/${projectId}/`)
        .then((r) => r.data.data),
    enabled: !!projectId && !isContractor,
  });

  function toggleSelect(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  if (isLoading) return <PageLoader />;

  function goToCluster(cluster: DocumentationCluster) {
    navigate(
      projectId
        ? `/projects/${projectId}/documentation/${cluster.id}`
        : isContractor
          ? `/contractor/documentation/${cluster.id}`
          : `/documentation/${cluster.id}`
    );
  }

  return (
    <div className="w-full px-2 sm:px-3 py-4 sm:py-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
            Dokumentacja projektowa
          </h1>
          {project && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {project.name}
            </p>
          )}
        </div>
        <div className="flex gap-2 sm:flex-shrink-0">
          {canManage && selectedIds.length > 0 && isDirector && (
            <>
              <Button
                onClick={() => setShowBilling(true)}
                variant="ghost"
                className="text-sm flex-1 sm:flex-initial"
              >
                Rozlicz ({selectedIds.length})
              </Button>
              <Button
                onClick={() => setShowMarkSettled(true)}
                variant="ghost"
                className="text-sm flex-1 sm:flex-initial"
              >
                Oznacz jako rozliczone ({selectedIds.length})
              </Button>
            </>
          )}
          {canCreateCluster && (
            <Button
              onClick={() => setShowCreate(true)}
              className="flex-1 sm:flex-initial"
            >
              Nowy klaster
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
        <div className="flex gap-1 rounded-md border border-gray-200 dark:border-gray-700 p-0.5 w-full sm:w-fit">
          {SIGNING_MODE_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setSigningModeFilter(value)}
              className={clsx(
                "rounded px-3 py-1.5 text-sm transition-colors flex-1 sm:flex-initial",
                signingModeFilter === value
                  ? "bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100 font-medium"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ClusterStatusFilter)}
          className={SELECT_CLASS}
        >
          {statusOptions.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        {!projectId && projectOptions.length > 1 && (
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">Wszystkie przedsięwzięcia</option>
            {projectOptions.map(([value, name]) => (
              <option key={value} value={value}>{name}</option>
            ))}
          </select>
        )}

        {signerOptions.length > 1 && (
          <select
            value={signerFilter}
            onChange={(e) => setSignerFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">Wszyscy sprawdzający</option>
            {signerOptions.map(([value, name]) => (
              <option key={value} value={value}>{name}</option>
            ))}
          </select>
        )}

        {(isDirector || isCEO) && authorOptions.length > 1 && (
          <select
            value={authorFilter}
            onChange={(e) => setAuthorFilter(e.target.value)}
            className={SELECT_CLASS}
          >
            <option value="">Wszyscy autorzy</option>
            {authorOptions.map(([value, name]) => (
              <option key={value} value={value}>{name}</option>
            ))}
          </select>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none px-1">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
            className="rounded h-4 w-4 focus:ring-2 focus:ring-blue-500"
          />
          Tylko moje
        </label>
      </div>

      {visibleClusters.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p>Brak klastrów dokumentacji.</p>
          {canManage && clusters.length === 0 && (
            <p className="text-sm mt-1">
              Kliknij „Nowy klaster" aby rozpocząć.
            </p>
          )}
        </div>
      ) : (
        <>
        {/* Mobile: card list */}
        <ul className="md:hidden space-y-2">
          {visibleClusters.map((cluster) => {
            const isPaper = cluster.signing_mode === "paper";
            const showCheckbox = canManage && isDirector;
            return (
              <li
                key={cluster.id}
                className={clsx(
                  "rounded-lg border shadow-sm active:scale-[0.99] transition-transform",
                  isPaper
                    ? "border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
                    : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
                )}
              >
                <div className="flex">
                  {showCheckbox && (
                    <label
                      className="flex items-start pl-3 pt-3 pr-1 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(cluster.id)}
                        onChange={() => toggleSelect(cluster.id)}
                        disabled={cluster.is_settled}
                        title={cluster.is_settled ? "Klaster jest już rozliczony" : undefined}
                        className="rounded h-4 w-4 disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => goToCluster(cluster)}
                    className="flex-1 text-left p-3 min-w-0"
                  >
                    <div className="flex items-start gap-2">
                      {isPaper ? (
                        <Printer className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
                      ) : cluster.cluster_type === "director_acceptance" ? (
                        <UserCheck className="h-4 w-4 mt-0.5 text-purple-500 dark:text-purple-400 shrink-0" />
                      ) : (
                        <FileSignature className="h-4 w-4 mt-0.5 text-blue-500 dark:text-blue-400 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 break-words">
                          {cluster.name}
                        </div>
                        {cluster.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {cluster.description}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {cluster.project_name}
                        </p>
                      </div>
                    </div>

                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                      <Badge
                        color={cluster.cluster_type === "director_acceptance" ? "purple" : "blue"}
                      >
                        {CLUSTER_TYPE_LABELS[cluster.cluster_type]}
                      </Badge>
                      <Badge color={clusterStatusColor(cluster)}>
                        {clusterStatusLabel(cluster)}
                      </Badge>
                      {cluster.is_settled && (
                        <Badge color="gray">Rozliczony</Badge>
                      )}
                      {canManage && cluster.pending_approval_count > 0 && (
                        <Badge color="orange">
                          {cluster.pending_approval_count} do akceptacji
                        </Badge>
                      )}
                      {canManage && cluster.unacknowledged_count > 0 && (
                        <Badge color="blue">
                          {cluster.unacknowledged_count} do odebrania
                        </Badge>
                      )}
                    </div>

                    <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div className="min-w-0">
                        <dt className="text-gray-400 dark:text-gray-500">Sprawdzający</dt>
                        <dd className="text-gray-700 dark:text-gray-300 truncate">
                          {signerName(cluster)}
                          {cluster.employee_signer_name && (
                            <span className="ml-1 text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400">
                              (prac.)
                            </span>
                          )}
                          {cluster.reviewing_director_name && (
                            <span className="ml-1 text-[10px] uppercase tracking-wide text-purple-600 dark:text-purple-400">
                              (dyr.)
                            </span>
                          )}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-gray-400 dark:text-gray-500">Autor</dt>
                        <dd className="text-gray-700 dark:text-gray-300 truncate">
                          {cluster.created_by_name ?? "—"}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-gray-400 dark:text-gray-500">Dokumenty</dt>
                        <dd className="text-gray-700 dark:text-gray-300">
                          {cluster.document_count}
                        </dd>
                      </div>
                    </dl>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Desktop: table */}
        <div
          ref={scrollRef}
          className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <table className="w-full min-w-[700px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
              <tr>
                {canManage && isDirector && (
                  <th className="px-3 py-2 text-left w-8"></th>
                )}
                <th className="px-4 py-2 text-left font-medium">Nazwa klastra</th>
                <th className="px-4 py-2 text-left font-medium">Przedsięwzięcie</th>
                <th className="px-4 py-2 text-left font-medium">Sprawdzający</th>
                <th className="px-4 py-2 text-left font-medium">Autor</th>
                <th className="px-4 py-2 text-left font-medium">Dokumenty</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {visibleClusters.map((cluster) => {
                const isPaper = cluster.signing_mode === "paper";
                return (
                  <tr
                    key={cluster.id}
                    className={clsx(
                      "hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer",
                      isPaper ? "bg-amber-50/50 dark:bg-amber-950/20" : "bg-white dark:bg-gray-900"
                    )}
                    onClick={() => goToCluster(cluster)}
                  >
                    {canManage && isDirector && (
                      <td
                        className="px-3 py-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(cluster.id)}
                          onChange={() => toggleSelect(cluster.id)}
                          disabled={cluster.is_settled}
                          title={cluster.is_settled ? "Klaster jest już rozliczony" : undefined}
                          className="rounded disabled:opacity-40 disabled:cursor-not-allowed"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-2">
                        {isPaper ? (
                          <Printer className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                        ) : cluster.cluster_type === "director_acceptance" ? (
                          <UserCheck className="h-3.5 w-3.5 text-purple-500 dark:text-purple-400 shrink-0" />
                        ) : (
                          <FileSignature className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
                        )}
                        {cluster.name}
                        {canManage && cluster.unacknowledged_count > 0 && (
                          <Badge color="blue">
                            {cluster.unacknowledged_count} do odebrania
                          </Badge>
                        )}
                      </div>
                      {cluster.description && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 font-normal truncate max-w-xs ml-5">
                          {cluster.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {cluster.project_name}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {signerName(cluster)}
                      {cluster.employee_signer_name && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400">
                          (pracownik)
                        </span>
                      )}
                      {cluster.reviewing_director_name && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-purple-600 dark:text-purple-400">
                          (dyrektor)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-500 text-xs">
                      {cluster.created_by_name ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {cluster.document_count}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge
                          color={cluster.cluster_type === "director_acceptance" ? "purple" : "blue"}
                        >
                          {CLUSTER_TYPE_LABELS[cluster.cluster_type]}
                        </Badge>
                        <Badge color={clusterStatusColor(cluster)}>
                          {clusterStatusLabel(cluster)}
                        </Badge>
                        {cluster.is_settled && (
                          <Badge color="gray">Rozliczony</Badge>
                        )}
                        {canManage && cluster.pending_approval_count > 0 && (
                          <Badge color="orange">
                            {cluster.pending_approval_count} do akceptacji
                          </Badge>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          ref={stickyScrollRef}
          className="hidden md:block sticky bottom-0 overflow-x-auto bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-1"
        >
          <div style={{ width: scrollWidth, height: 1 }} />
        </div>
        </>
      )}

      {showCreate && (
        <CreateClusterDialog
          projectId={projectId}
          onClose={() => setShowCreate(false)}
          onCreated={() => refetch()}
        />
      )}

      {showBilling && (
        <BillingOrderDialog
          clusters={clusters}
          selectedClusterIds={selectedIds}
          onClose={() => setShowBilling(false)}
          onCreated={() => {
            setSelectedIds([]);
            refetch();
          }}
        />
      )}

      {showMarkSettled && (
        <MarkSettledDialog
          clusters={clusters}
          selectedClusterIds={selectedIds}
          onClose={() => setShowMarkSettled(false)}
          onSettled={() => {
            setSelectedIds([]);
            refetch();
          }}
        />
      )}
    </div>
  );
}
